import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface FilePreviewProps {
  file: File | null;
  onRemove?: () => void;
  className?: string;
}

export const FilePreview: React.FC<FilePreviewProps> = ({ file, onRemove, className = '' }) => {
  const [preview, setPreview] = useState<string>('');
  const [fileType, setFileType] = useState<'image' | 'video' | 'audio' | '3d' | 'other'>('other');

  useEffect(() => {
    if (!file) {
      setPreview('');
      return;
    }

    // Determine file type
    if (file.type.startsWith('image/')) {
      setFileType('image');
    } else if (file.type.startsWith('video/')) {
      setFileType('video');
    } else if (file.type.startsWith('audio/')) {
      setFileType('audio');
    } else if (file.name.endsWith('.glb') || file.name.endsWith('.gltf')) {
      setFileType('3d');
    } else {
      setFileType('other');
    }

    // Create preview URL
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);

    // Clean up
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (!file || !preview) {
    return null;
  }

  return (
    <div className={`relative rounded-lg overflow-hidden ${className}`}>
      {onRemove && (
        <button
          onClick={onRemove}
          className="absolute top-2 right-2 p-1 rounded-full text-white transition-colors z-10"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
          }}
        >
          <X size={16} />
        </button>
      )}
      
      {fileType === 'image' && (
        <img
          src={preview}
          alt="Preview"
          className="w-full h-full object-cover rounded-lg"
        />
      )}
      
      {fileType === 'video' && (
        <video
          src={preview}
          controls
          className="w-full h-full object-cover rounded-lg"
        />
      )}
      
      {fileType === 'audio' && (
        <div 
          className="w-full h-full min-h-[100px] rounded-lg flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--color-card-bg)' }}
        >
          <audio src={preview} controls className="w-full" />
        </div>
      )}
      
      {fileType === '3d' && (
        <div 
          className="w-full h-full min-h-[200px] rounded-lg flex flex-col items-center justify-center p-4"
          style={{ backgroundColor: 'var(--color-card-bg)' }}
        >
          <p className="text-sm" style={{ color: 'var(--color-text)' }}>3D Preview Not Available</p>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text)', opacity: 0.7 }}>{file.name}</p>
        </div>
      )}
      
      {fileType === 'other' && (
        <div 
          className="w-full h-full min-h-[100px] rounded-lg flex flex-col items-center justify-center p-4"
          style={{ backgroundColor: 'var(--color-card-bg)' }}
        >
          <p className="text-sm" style={{ color: 'var(--color-text)' }}>Preview Not Available</p>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text)', opacity: 0.7 }}>{file.name}</p>
        </div>
      )}
    </div>
  );
};
